import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { ChangeEvent, useEffect, useMemo, useRef, useState } from 'react';
import { useNotify, useTranslate } from 'react-admin';
import { Iconify } from 'src/components/iconify';
import { errorMessage } from '../../perps-v1-shared/errorMessage';
import { parseDisplayMetaCsv } from '../csv';
import { ImportDisplayEntry, symbolsApi } from '../dataProvider';

interface Props {
  open: boolean;
  onClose: () => void;
  onImported: () => void;
}

export default function ImportDialog({ open, onClose, onImported }: Props) {
  const translate = useTranslate();
  const notify = useNotify();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [text, setText] = useState('');
  const [importing, setImporting] = useState(false);

  useEffect(() => {
    if (open) {
      setText('');
    }
  }, [open]);

  const parsed = useMemo(() => parseDisplayMetaCsv(text), [text]);
  const canImport = parsed.errors.length === 0 && parsed.rows.length > 0;

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => setText(String(reader.result ?? ''));
      reader.readAsText(file);
    }
    // Reset the input value so re-selecting the same file still fires onChange.
    event.target.value = '';
  };

  const handleImport = async () => {
    if (!canImport) return;
    setImporting(true);
    try {
      const entries: ImportDisplayEntry[] = parsed.rows.map((row) => ({
        symbol: row.symbol,
        name: { en: row.nameEn, zh: row.nameZh },
      }));
      const response = await symbolsApi.importDisplay(entries);
      notify(translate('module.perpsV1Symbols.importSuccess', { count: response?.count ?? entries.length }), {
        type: 'success',
      });
      onImported();
      onClose();
    } catch (error: any) {
      notify(errorMessage(error, translate('module.perpsV1Shared.saveError')), {
        type: 'error',
      });
    } finally {
      setImporting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{translate('module.perpsV1Symbols.importTitle')}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            {translate('module.perpsV1Symbols.importHint')}
          </Typography>

          <Button
            variant="outlined"
            component="label"
            startIcon={<Iconify icon="eva:upload-outline" />}
            sx={{ alignSelf: 'flex-start' }}
          >
            {translate('module.perpsV1Symbols.chooseFile')}
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,text/csv"
              hidden
              onChange={handleFileChange}
            />
          </Button>

          <TextField
            multiline
            minRows={8}
            fullWidth
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={translate('module.perpsV1Symbols.importPlaceholder')}
            inputProps={{ style: { fontFamily: 'monospace', fontSize: 13 } }}
          />

          <Typography variant="body2">
            {translate('module.perpsV1Symbols.importPreview', { count: parsed.rows.length })}
          </Typography>

          {parsed.errors.length > 0 ? (
            <Stack spacing={0.5}>
              <Typography variant="body2" color="error">
                {translate('module.perpsV1Symbols.importParseErrors')}
              </Typography>
              {parsed.errors.map((error) => (
                <Typography key={error} variant="caption" color="error">
                  {error}
                </Typography>
              ))}
            </Stack>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <LoadingButton onClick={onClose} disabled={importing}>
          {translate('actions.close')}
        </LoadingButton>
        <LoadingButton variant="contained" onClick={handleImport} loading={importing} disabled={!canImport}>
          {translate('module.perpsV1Symbols.import')}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
